import { ForwardedRef, forwardRef, memo, useContext, useRef } from "react"
import {
  type Message as MessageType,
  MessageDisplayType,
  MessageGroup,
  SlickAIGroup,
} from "@/reducers/messagesSlice"
import DayMarker from "../DayMarker"
import {
  areMessageFromDifferentDay,
  getCurrentMessageDate,
} from "@/lib/messageDate"
import ChatHelpers from "../../lib/chatHelpers"
import { isObject } from "lodash"
import GenericMessage from "@/components/chat/GenericMessage"
import GroupedImagesMessage from "@/components/chat/GroupedImagesMessage"
import SlickAIMessage from "./SlickAIMessage"
import RetractedMessage from "./RetractedMessage"
import GroupedForwardedImagesMessage from "@/components/chat/GroupedForwardedImagesMessage"
import { MESSAGE_HIGHLIGHT_DURATION_MS } from "@/components/ChatHistory"
import type { ComposerSurface } from "@/reducers/chatRepliesSlice"
import { cn } from "@/lib/utils"
import MissedCallMessage from "@/components/chat/MisssedCallMessage"
import { useMessageRowState } from "@/components/chat/useMessageRowState"
import { isMobile } from "@/utils/detectDevice"
import { useOnClickOutside } from "usehooks-ts"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import { Checkbox } from "@/components/ui/checkbox"
import type { AccountChat } from "@/lib/accountChatsParser"

export type MessageRowType = MessageType | MessageGroup | undefined
/** Neighbours may be a collapsed SlickAI group, which never renders as a
 * `MessageRow` itself but still counts for day markers and 5-minute grouping. */
export type MessageNeighbourType = MessageRowType | SlickAIGroup
export type MessageRowsType = [
  MessageNeighbourType,
  NonNullable<MessageRowType>,
  MessageNeighbourType,
]
type MessageRowProps = {
  previousRow: MessageNeighbourType
  row: NonNullable<MessageRowType>
  nextRow: MessageNeighbourType
  accountChat?: AccountChat
  isDriverRoomChat?: boolean
  className?: string
  composerSurface?: ComposerSurface
}

const MessageRow = forwardRef<HTMLDivElement, MessageRowProps>(
  (
    {
      previousRow,
      row,
      nextRow,
      accountChat,
      isDriverRoomChat,
      composerSurface,
      ...props
    }: MessageRowProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      grouped,
      isLastInGrouped,
      isFirstInGrouped,
      messageForRowActions,
      isHighlighted,
      messageOrMessageGroup,
      previousMessageOrMessageGroup,
      onLongPress,
      actionButtonsVisible,
      hideActionButtons,
    } = useMessageRowState([previousRow, row, nextRow], accountChat)
    const rowRef = useRef<HTMLDivElement>(null)
    const { isForwardingMode, selectedMessageIds, toggleMessageSelection } =
      useContext(ChatHistoryContext)

    // Handle selection for both individual messages and message groups
    const messageIds =
      messageOrMessageGroup.displayType === MessageDisplayType.MessageGroup
        ? messageOrMessageGroup.messages.map((m) => m.id)
        : messageOrMessageGroup.id
          ? [messageOrMessageGroup.id]
          : []

    const isSelected =
      messageIds.length > 0 &&
      messageIds.every((id) => selectedMessageIds.has(id))

    const handleToggleSelection = () => {
      if (messageIds.length > 0) {
        toggleMessageSelection(messageIds)
      }
    }
    const renderMessageComponent = () => {
      // Handle grouped images/files (MessageGroup)
      if (
        messageOrMessageGroup.displayType === MessageDisplayType.MessageGroup
      ) {
        if (messageOrMessageGroup.messages.every((m) => m.forwarded)) {
          return (
            <GroupedForwardedImagesMessage
              messagesGroup={messageOrMessageGroup}
              actionButtonsVisible={actionButtonsVisible}
              hideActionButtons={hideActionButtons}
            />
          )
        }
        return (
          <GroupedImagesMessage
            messagesGroup={messageOrMessageGroup}
            actionButtonsVisible={actionButtonsVisible}
            hideActionButtons={hideActionButtons}
          />
        )
      }

      // Handle not grouped messages (Message)
      if (messageOrMessageGroup.displayType === MessageDisplayType.Message) {
        // Retracted messages
        if (messageOrMessageGroup.retracted) {
          return <RetractedMessage message={messageOrMessageGroup} />
        }

        if (messageOrMessageGroup.missedCallNumber) {
          return <MissedCallMessage message={messageOrMessageGroup} />
        }

        // SlickAI messages
        if (ChatHelpers.isMessageFromSlickAI(messageOrMessageGroup.from)) {
          return (
            <SlickAIMessage
              message={messageOrMessageGroup}
              grouped={grouped}
              isLastInGrouped={isLastInGrouped}
              actionButtonsVisible={actionButtonsVisible}
              hideActionButtons={hideActionButtons}
              tightenGapAbove={isConsecutiveSameDaySlickAI}
              composerSurface={composerSurface}
            />
          )
        }

        // Normal messages (default case)
        return (
          <GenericMessage
            message={messageOrMessageGroup}
            grouped={grouped}
            isFirstInGrouped={isFirstInGrouped}
            isLastInGrouped={isLastInGrouped}
            actionButtonsVisible={actionButtonsVisible}
            hideActionButtons={hideActionButtons}
            composerSurface={composerSurface}
          />
        )
      }

      return null
    }

    // If there is no previous message, it's the first message,
    // so we want to have a marker. Return true.
    const isDifferentDay =
      isObject(previousMessageOrMessageGroup) &&
      isObject(messageOrMessageGroup) &&
      areMessageFromDifferentDay(
        messageOrMessageGroup,
        previousMessageOrMessageGroup,
      )

    // Two Slick AI automation messages back to back, same day — still the
    // same conversation, so the extra breathing room that sets the AI layer
    // apart from driver bubbles isn't needed between them.
    const isConsecutiveSameDaySlickAI =
      !isDifferentDay &&
      Boolean(
        (previousMessageOrMessageGroup as MessageType | undefined)?.internal,
      ) &&
      messageOrMessageGroup.displayType === MessageDisplayType.Message &&
      Boolean(messageOrMessageGroup.internal)

    useOnClickOutside(rowRef, hideActionButtons)

    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          isMobile && "[touch-callout:none]",
          !nextRow && "mb-6",
          props.className,
        )}
        {...(isMobile ? onLongPress : {})}
      >
        {isDifferentDay && (
          <DayMarker
            date={getCurrentMessageDate(messageOrMessageGroup)}
            isDriverRoomChat={isDriverRoomChat}
          />
        )}
        {isForwardingMode ? (
          <div
            className={cn(
              "flex items-start gap-2",
              isSelected && "bg-blue-50/60 dark:bg-blue-900/30",
            )}
          >
            <div className="ml-2 flex items-center self-center pt-1">
              <Checkbox
                checked={isSelected}
                onCheckedChange={handleToggleSelection}
                className="data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-500"
              />
            </div>
            <div
              ref={rowRef}
              data-testid={messageForRowActions.id}
              style={{
                animationDuration: `${MESSAGE_HIGHLIGHT_DURATION_MS}ms`,
              }}
              className={cn(
                isHighlighted &&
                  "animate-[message-bg-fade] [&>div]:animate-[highlight-message_0.5s_linear_0.1s]",
                "flex-1", // Take remaining space when checkbox is present
              )}
            >
              {renderMessageComponent()}
            </div>
          </div>
        ) : (
          <div
            ref={rowRef}
            data-testid={messageForRowActions.id}
            style={{ animationDuration: `${MESSAGE_HIGHLIGHT_DURATION_MS}ms` }}
            className={cn(
              isHighlighted &&
                "animate-[message-bg-fade] [&>div]:animate-[highlight-message_0.5s_linear_0.1s]",
            )}
          >
            {renderMessageComponent()}
          </div>
        )}
      </div>
    )
  },
)

MessageRow.displayName = "MessageRow"

export default memo(MessageRow)
